/**
 * Акт сверки — §4 дополнения о сверке, D262, D263.
 *
 * Строки акта с парами у нас, расхождения словами и итог по сальдо:
 * объясняют ли строки всю разницу долга. Расхождение закрывается кнопкой
 * «Разобрались» только с причиной — оно уходит из подсветки, но остаётся
 * в истории акта.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@shell/api';
import { ErrorState, Loading } from '@shell/states';
import type { Navigation } from '../../api';
import { rub } from '../../api';
import { Icon } from '../../icons';
import { canSee } from '../../nav';
import { Link, navigate } from '../../router';
import { Notice, isoDate, when } from '../../ui';
import { cardPath } from '../payments/paths';
import { LIVE, issuesText, reconcileApi } from './api';
import type { ActLine, ActView, LineKind, Resolved } from './api';
import { ActBadge } from './Suppliers';
import { TAB_PATHS, ledgerPath } from './paths';

const LINE_KIND: Record<LineKind, string> = {
  delivery: 'отгрузка',
  payment: 'оплата',
  return: 'возврат',
  adjustment: 'корректировка',
  other: 'прочее',
};

function actKey(id: number) {
  return ['reconcile', 'act', id] as const;
}

function openIssues(act: ActView): number {
  return (
    act.lines.filter((line) => line.issue && !line.issue.resolved).length +
    act.extra.filter((item) => !item.resolved).length
  );
}

export function ActScreen({ id, nav }: { id: number; nav: Navigation }) {
  const client = useQueryClient();
  const seesPayments = canSee('payments', nav);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const query = useQuery<ActView, ApiError>({
    queryKey: actKey(id),
    queryFn: () => reconcileApi.act(id),
    ...LIVE,
  });

  const resolve = useMutation<ActView, ApiError, { key: string; reason: string }>({
    mutationFn: ({ key, reason }) => reconcileApi.resolve(id, key, reason),
    onSuccess: (act) => {
      client.setQueryData(actKey(id), act);
      client.invalidateQueries({ queryKey: ['reconcile', 'overview'] });
      client.invalidateQueries({ queryKey: ['reconcile', 'ledger'] });
      setResult({ ok: true, text: 'Записано. Расхождение ушло из подсветки и осталось в истории акта.' });
    },
    onError: (error) => setResult({ ok: false, text: error.detail ?? 'Не получилось. Попробуйте ещё раз.' }),
  });

  const remove = useMutation<{ message: string; supplier_id: number }, ApiError, void>({
    mutationFn: () => reconcileApi.deleteAct(id),
    onSuccess: (answer) => {
      client.removeQueries({ queryKey: actKey(id) });
      client.invalidateQueries({ queryKey: ['reconcile'] });
      navigate(ledgerPath(answer.supplier_id));
    },
    onError: (error) => {
      setDeleting(false);
      setResult({ ok: false, text: error.detail ?? 'Акт не удалился. Попробуйте ещё раз.' });
    },
  });

  const supplierId = query.data?.supplier_id;
  const back = (
    <button
      type="button"
      className="rc-back"
      onClick={() => navigate(supplierId ? ledgerPath(supplierId) : TAB_PATHS.suppliers)}
    >
      <Icon name="back" size={16} /> {supplierId ? 'Лента поставщика' : 'Все поставщики'}
    </button>
  );

  if (query.isPending) {
    return (
      <>
        {back}
        <Loading />
      </>
    );
  }
  if (query.error) {
    return (
      <>
        {back}
        {query.error.status === 404 ? (
          <ErrorState title="Акта нет" text="Его могли удалить. Вернитесь к ленте поставщика." />
        ) : (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        )}
      </>
    );
  }

  const act = query.data;
  const pending = openIssues(act);
  const t = act.totals;
  const canResolve = act.can_edit;
  const busy = resolve.isPending;

  return (
    <div className="rc-stack">
      {back}
      <section className="rc-card" aria-label="Акт сверки">
        <header className="rc-card__head">
          <div>
            <h2 className="rc-card__title">
              Акт{act.number ? ` № ${act.number}` : ' без номера'}
              {act.act_date ? ` от ${isoDate(act.act_date)}` : ''}
            </h2>
            <p className="faint">
              {act.period_from && act.period_to
                ? `Период ${isoDate(act.period_from)}–${isoDate(act.period_to)} · `
                : ''}
              поставщик <Link to={ledgerPath(act.supplier_id)}>{act.supplier}</Link>
            </p>
          </div>
          <ActBadge status={act.status} issues={pending} />
        </header>

        {act.read_state !== 'full' && (
          <p className="rc-note">
            {act.read_note ??
              'Акт прочитан не полностью, и сверка по нему не считается. Пришлите акт другим файлом — лучше выгрузкой из программы поставщика, а не сканом.'}
          </p>
        )}

        {t.act_closing !== null && t.our_closing !== null && (
          <p className="rc-totals">
            По акту долг{act.act_date ? ` на ${isoDate(act.act_date)}` : ''}: <strong>{rub(t.act_closing)}</strong>.
            По нашему учёту: <strong>{rub(t.our_closing)}</strong>.{' '}
            {t.diff === null || Number(t.diff) === 0 ? (
              'Долги совпадают.'
            ) : (
              <>
                Разница {rub(t.diff)} —{' '}
                {t.explained_fully ? (
                  'целиком объяснена строками ниже.'
                ) : (
                  <span className="rc-unexplained">необъяснённый остаток {rub(t.unexplained)}.</span>
                )}
              </>
            )}
          </p>
        )}

        <p className="rc-todo">
          {act.read_state !== 'full'
            ? 'Сначала нужен полностью прочитанный акт — иначе расхождения будут ложными.'
            : pending > 0
              ? `Осталось разобрать: ${issuesText(pending)}. Найдите бумагу и сдайте её фото в «Накладных», уточните у поставщика — и нажмите «Разобрались» с причиной.`
              : 'Акт сошёлся — делать ничего не нужно.'}
        </p>

        {result && <Notice kind={result.ok ? 'ok' : 'error'}>{result.text}</Notice>}

        <div className="rc-actions">
          {act.file_url && (
            <a className="button button--small" href={act.file_url} target="_blank" rel="noreferrer">
              <Icon name="external" size={14} /> Открыть файл акта
            </a>
          )}
          {act.can_edit &&
            (deleting ? (
              <span className="rc-confirm rc-confirm--inline">
                <span>Удалить акт? Строки и разборы по нему пропадут.</span>
                <button
                  type="button"
                  className="button button--danger button--small"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate()}
                >
                  Да, удалить
                </button>
                <button type="button" className="button button--small" onClick={() => setDeleting(false)}>
                  Отмена
                </button>
              </span>
            ) : (
              <button type="button" className="button button--quiet button--small" onClick={() => setDeleting(true)}>
                <Icon name="trash" size={14} /> Удалить акт
              </button>
            ))}
        </div>
      </section>

      <section className="rc-card" aria-label="Строки акта">
        <h3 className="block-title">Строки акта</h3>
        {act.lines.length === 0 ? (
          <p className="faint">Строк в акте не прочитано.</p>
        ) : (
          <div className="rc-table rc-act" role="table" aria-label="Строки акта и пары у нас">
            <div className="rc-act__row rc-table__head" role="row">
              <span role="columnheader">Дата</span>
              <span role="columnheader">Документ</span>
              <span role="columnheader" className="num">Сумма</span>
              <span role="columnheader">Пара у нас</span>
              <span role="columnheader">Расхождение</span>
            </div>
            {act.lines.map((line) => (
              <ActLineRow
                key={line.id}
                line={line}
                seesPayments={seesPayments}
                canResolve={canResolve}
                busy={busy}
                onResolve={(key, reason) => resolve.mutate({ key, reason })}
              />
            ))}
          </div>
        )}
      </section>

      {act.extra.length > 0 && (
        <section className="rc-card" aria-label="Есть у нас, нет в акте">
          <h3 className="block-title">Есть у нас, нет в акте</h3>
          <ul className="rc-plain-list">
            {act.extra.map((item) => (
              <li
                key={item.key}
                className={`rc-extra${item.resolved ? ' rc-extra--resolved' : ' rc-extra--open'}`}
              >
                <span className="rc-unused__main">
                  <span className="rc-issue__text">{item.text}</span>
                  <span className="faint">
                    {isoDate(item.date)} · {rub(item.amount)}
                    {item.ref_id !== null && seesPayments && item.key.startsWith('invoice:') ? (
                      <>
                        {' · '}
                        <Link to={cardPath(item.ref_id)}>открыть накладную</Link>
                      </>
                    ) : null}
                  </span>
                </span>
                <IssueState
                  issueKey={item.key}
                  resolved={item.resolved}
                  canResolve={canResolve}
                  busy={busy}
                  onResolve={(key, reason) => resolve.mutate({ key, reason })}
                />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function ActLineRow({
  line,
  seesPayments,
  canResolve,
  busy,
  onResolve,
}: {
  line: ActLine;
  seesPayments: boolean;
  canResolve: boolean;
  busy: boolean;
  onResolve: (key: string, reason: string) => void;
}) {
  const open = Boolean(line.issue && !line.issue.resolved);
  const match = line.match;
  return (
    <div
      className={`rc-act__row rc-act-line${open ? ' rc-act-line--issue' : ''}${
        line.issue?.resolved ? ' rc-act-line--resolved' : ''
      }`}
      role="row"
    >
      <span role="cell">{line.day ? isoDate(line.day) : '—'}</span>
      <span role="cell" className="rc-act-line__doc">
        <span className="rc-row__kind">{LINE_KIND[line.kind]}</span>
        {line.document}
      </span>
      <span role="cell" className="num" data-label="Сумма">
        {rub(line.amount)}
      </span>
      <span role="cell" data-label="Пара у нас">
        {match ? (
          match.type === 'invoice' && seesPayments ? (
            <Link to={cardPath(match.id)}>{match.label}</Link>
          ) : (
            match.label
          )
        ) : (
          <span className="faint">нет пары</span>
        )}
      </span>
      <span role="cell" className="rc-act-line__issue">
        {line.issue ? (
          <>
            <span className="rc-issue__text">{line.issue.text}</span>
            <IssueState
              issueKey={line.issue.key}
              resolved={line.issue.resolved}
              canResolve={canResolve}
              busy={busy}
              onResolve={onResolve}
            />
          </>
        ) : (
          <span className="faint">—</span>
        )}
      </span>
    </div>
  );
}

/** «Разобрались» с обязательной причиной — или уже записанная причина. */
function IssueState({
  issueKey,
  resolved,
  canResolve,
  busy,
  onResolve,
}: {
  issueKey: string;
  resolved: Resolved | null;
  canResolve: boolean;
  busy: boolean;
  onResolve: (key: string, reason: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  if (resolved) {
    return (
      <span className="rc-resolved">
        Разобрались: {resolved.reason}
        {resolved.by ? ` — ${resolved.by}` : ''}
        {resolved.at ? `, ${when(resolved.at)}` : ''}
      </span>
    );
  }
  if (!canResolve) return null;
  if (!open) {
    return (
      <button type="button" className="button button--small rc-resolve" disabled={busy} onClick={() => setOpen(true)}>
        Разобрались
      </button>
    );
  }
  return (
    <form
      className="rc-inline-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (!reason.trim()) return;
        onResolve(issueKey, reason.trim());
        setOpen(false);
        setReason('');
      }}
    >
      <input
        className="input"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Причина: «ошибка поставщика, исправит», «накладная сдана»"
        aria-label="Причина"
        autoFocus
      />
      <button type="submit" className="button button--primary button--small" disabled={!reason.trim() || busy}>
        Сохранить
      </button>
      <button type="button" className="button button--small" onClick={() => setOpen(false)}>
        Отмена
      </button>
    </form>
  );
}
